import { Button } from '@bennows/boows-react';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { PageToolsContext } from '../../components/page-header/page-tools';
import { useMediaQuery } from '../../components/data-table/useMediaQuery';
import { AccountArea } from '../auth/AccountArea';
import { CreditBanner } from '../billing/CreditBanner';
import { TrialBanner } from '../billing/TrialBanner';
import { CreditPill } from '../billing/CreditPill';
import { FeedbackButton } from '../feedback/FeedbackButton';
import { NotificationBell } from '../notifications/NotificationBell';
import { SearchOverlay } from '../search/SearchOverlay';
import { useSearchShortcut } from '../search/useSearchShortcut';
import { BottomTabs } from './BottomTabs';
import { BrandLogo } from './BrandLogo';
import { NAV_GROUP_ORDER, PRIMARY_NAV, type NavEntry } from './navigation';
import { useActiveTabInView } from './useActiveTabInView';
import { VersionIndicator } from './VersionIndicator';

/** Frames to wait for a lazily loaded page heading before focusing `main` instead. */
const HEADING_WAIT_FRAMES = 30;
/** Frames (about ten seconds) after which a heading that still has not arrived is no longer awaited. */
const HEADING_GIVE_UP_FRAMES = 600;

/** Below this width the frame is the phone frame: no top bar, the bottom tab bar (S150-1, S150-8). */
const PHONE_QUERY = '(max-width: 1023px)';

/**
 * The frame of every screen (spec 05 §6, style guide 2.10): skip link; from 1024 px a top bar with
 * the credit balance in its centre, search and the bell above the main column and the primary
 * navigation as a sidebar; below 1024 px no top bar - the same three tools sit in each page header's
 * top row (S150-8) - and the bottom tab bar (S150-1). Search opens as an overlay over the page.
 * `main#main`, footer with the API version. The account area (name, tenant switcher, sign-out)
 * sits below the sidebar navigation from 1024 px and on the "Mehr" page below.
 */
export function AppShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const phone = useMediaQuery(PHONE_QUERY);
  const [searching, setSearching] = useState(false);
  const openSearch = useCallback(() => setSearching(true), []);
  const closeSearch = useCallback(() => setSearching(false), []);
  useSearchShortcut(openSearch);
  useActiveTabInView();
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  const renderNavEntry = ({ key, path, icon: Icon, badge: Badge, end }: NavEntry) => (
    <NavLink key={key} to={path} end={end} className="ui-nav-item">
      <Icon aria-hidden="true" />
      {t(`nav.${key}`)}
      {Badge ? <Badge /> : null}
    </NavLink>
  );

  // After a navigation, focus the new page's heading so screen readers
  // announce which page was opened (falls back to main while a chunk loads).
  // In-page navigations such as switching tabs pass `state: { keepFocus: true }`
  // and keep focus on the control that caused them.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if ((location.state as { keepFocus?: boolean } | null)?.keepFocus) return;
    // A new page starts at the top: the router keeps the old scroll offset, and focusing the heading
    // must not scroll it into view by itself either (both made the content jump, S130-1, T130-1-8).
    window.scrollTo(0, 0);
    let frames = 0;
    let handle = 0;
    const focusHeading = () => {
      const heading = mainRef.current?.querySelector<HTMLElement>('h1');
      // A slow chunk: main holds focus meanwhile, and the heading still takes it over when it arrives,
      // as long as nobody moved focus elsewhere in between.
      if (heading) {
        if (frames < HEADING_WAIT_FRAMES || document.activeElement === mainRef.current) heading.focus({ preventScroll: true });
        return;
      }
      if (++frames === HEADING_WAIT_FRAMES) mainRef.current?.focus({ preventScroll: true });
      if (frames >= HEADING_GIVE_UP_FRAMES) return;
      handle = requestAnimationFrame(focusHeading);
    };
    focusHeading();
    return () => cancelAnimationFrame(handle);
  }, [location.pathname]);

  const searchButton = (
    <>
      <Button variant="ghost" iconOnly aria-label={t('shell.searchOpen')} aria-keyshortcuts="/" aria-haspopup="dialog" onClick={openSearch}>
        <Search aria-hidden="true" />
      </Button>
      <NotificationBell />
    </>
  );
  const pageTools = phone ? (
    <>
      <CreditPill />
      {searchButton}
    </>
  ) : null;

  return (
    <div className="ui-shell">
      <a className="ui-skip-link" href="#main">
        {t('shell.skipLink')}
      </a>

      {phone ? null : (
        <header className="ui-app-header">
          <NavLink to="/" className="ui-brand ui-app-header__brand">
            <BrandLogo />
          </NavLink>
          <div className="ui-app-header__centre">
            <CreditPill />
          </div>
          <div className="ui-app-header__tools">{searchButton}</div>
        </header>
      )}

      <aside className="ui-sidebar">
        <NavLink to="/" className="ui-brand">
          <BrandLogo />
        </NavLink>
        <nav aria-label={t('shell.navLabel')} className="ui-shell__nav">
          {NAV_GROUP_ORDER.map((groupKey) => (
            <div key={groupKey} className="ui-nav-group">
              {groupKey === 'more' ? <p className="ui-nav-group__label">{t('nav.group.more')}</p> : null}
              <div className="ui-nav-group__list">
                {PRIMARY_NAV.filter((entry) => entry.group === groupKey).map(renderNavEntry)}
                {groupKey === 'more' ? (
                  <NavLink to="/search" className="ui-nav-item" aria-keyshortcuts="/">
                    <Search aria-hidden="true" />
                    {t('nav.search')}
                  </NavLink>
                ) : null}
              </div>
            </div>
          ))}
          <div className="ui-nav-group__list ui-nav-group__list--standalone">
            {PRIMARY_NAV.filter((entry) => !entry.group).map(renderNavEntry)}
          </div>
        </nav>
        <AccountArea />
      </aside>

      <main id="main" ref={mainRef} tabIndex={-1} className="ui-shell__main">
        <TrialBanner />
        <CreditBanner />
        <PageToolsContext.Provider value={pageTools}>
          <Outlet />
        </PageToolsContext.Provider>
        <footer className="ui-shell__footer">
          <VersionIndicator />
        </footer>
      </main>

      <BottomTabs />
      <FeedbackButton />
      {searching ? <SearchOverlay onClose={closeSearch} /> : null}
    </div>
  );
}
